<template>
  <form class="form-container" @submit.prevent="submitForm">
    <section
      v-if="remainingVerificationAttempts == 0"
      class="error"
      id="verificationError"
    >
      <p>
        Your {{ maxVerificationAttempts }} verification attempts have been
        exhausted. Please contact {{ hipaaSafePracticeName }}.
      </p>
    </section>
    <p v-else>
      Please confirm your date of birth and name to view your secure medical
      information.
    </p>

    <section
      v-if="
        maxVerificationAttempts > remainingVerificationAttempts &&
        remainingVerificationAttempts != 0
      "
      class="warning"
      id="verificationWarn"
    >
      <p>
        Verification failed. You have
        {{ remainingVerificationAttempts }} attempts remaining.
      </p>
    </section>

    <div class="form-body">
      <div
        v-for="field in formsData.fields"
        :key="field.id + field.type"
        class="form-field"
      >
        <date-of-birth-field
          :field="field"
          :show-error="errorChange"
          v-if="field.type === 'dateOfBirth'"
          :disabled="formElementsDisabled"
          :value="field.value"
          @input="dateOfBirthHandler"
          @year="yearHandler"
        />
        <text-box-field
          :field="field"
          :show-error="errorChange"
          v-if="field.type === 'textbox'"
          :disabled="formElementsDisabled"
          :handle-focus="forceFocus"
        />
      </div>
    </div>
    <section class="form-actions">
      <updox-button
        type="submit"
        text="Verify Identity"
        aria-label="Verify Identity"
        id="submitFormButton"
        :disabled="submitDisabled || formElementsDisabled"
        :loading="currentlySubmitting"
      />
    </section>
  </form>
</template>

<script>
  import TextBoxField from "./common/TextBoxField";
  import DateOfBirthField from "./common/DateOfBirthField";
  import SimplePaymentsPatientService from "../services/SimplePaymentsPatientService";
  import { UpdoxButton }  from "@updox/common-vue";
  import moment from "moment";

  export default {
    name: "PaymentValidateForm",
    components: {
      DateOfBirthField,
      TextBoxField,
      UpdoxButton,
    },
    props: {
      formsData: {
        fields: [],
      },
    },
    data: () => ({
      submitted: false,
      error: false,
      submitDisabled: true,
      currentlySubmitting: false,
      errorChange: false,
      forceFocus: {
        fieldId: "",
        sequence: 0,
      },
    }),
    computed: {
      form: function () {
        if (this.formsData !== undefined && this.formsData !== null) {
          return this.formsData;
        } else {
          return null;
        }
      },
      remainingVerificationAttempts() {
        return this.$store.getters.remainingVerificationAttempts;
      },
      maxVerificationAttempts() {
        return this.$store.getters.maxVerificationAttempts;
      },
      hipaaSafePracticeName() {
        let name = this.$store.getters.hipaaSafePracticeName;
        if (name === "medical provider" || name === "pharmacy") {
          return "your " + name;
        }
        return name;
      },
      formElementsDisabled() {
        return (
          this.currentlySubmitting ||
          this.$store.getters.remainingVerificationAttempts === 0
        );
      },
    },
    watch: {
      form: {
        handler() {
          this.checkIfSubmittable();
        },
        deep: true,
      },
    },
    methods: {
      dateOfBirthHandler(dateOfBirth) {
        /**
         *  Ensure we have a valid moment date object as well as actual values passed from DateOfBirthField.
         *  moment.js validation with isValid().
         **/
        const dateIsValid = moment(
          `${dateOfBirth.year}-${dateOfBirth.month}-${dateOfBirth.day}`,
          "YYYY-MM-DD",
          true
        ).isValid();

        if (dateIsValid) {
          this.form.fields[0].value = `${dateOfBirth.year}-${dateOfBirth.month}-${dateOfBirth.day}`;
        } else {
          this.form.fields[0].value = "";
        }
      },
      yearHandler() {
        this.updateForceFocus(this.form.fields[1].id);
      },
      submitForm() {
        console.debug("submitting... ", {
          dateOfBirth: this.form.fields[0].value,
          firstName: this.form.fields[1].value,
          lastName: this.form.fields[2].value,
        });

        this.currentlySubmitting = true;

        const { paymentRequestToken } = this.$route.params;
        const paymentVerificationRequest = {
          firstName: this.form.fields[1].value,
          lastName: this.form.fields[2].value,
          dateOfBirth: this.form.fields[0].value,
        };

        // If datOfBirth was fat fingered, and is in the future, save them from themselves...
        const dateOfBirth = moment(paymentVerificationRequest.dateOfBirth); // Should already be in YYYY-MM-DD format
        const now = moment();
        if (dateOfBirth >= now) {
          this.currentlySubmitting = false;
          return window.alert(
            "Date of Birth entered is greater than today's date. Are you from the future? We can't verify time travelers."
          );
        }

        SimplePaymentsPatientService.validatePaymentRequest(
          paymentRequestToken,
          paymentVerificationRequest
        )
          .then((response) => {
            if (response) {
              if (response.status == "VERIFIED") {
                this.$router.push(`/remit/${paymentRequestToken}`);
              } else if (
                response.status == "NOT_VERIFIED" ||
                response.status == "LOCKED"
              ) {
                this.$store.commit(
                  "setRemainingVerificationAttempts",
                  response?.remainingVerificationAttempts
                );
              } else {
                this.$emit("parent-status", response?.status);
              }
            }
          })
          .catch((e) => {
            /*airbrake.notify({
               error: e,
               message: "Unable to process payment token"
            });*/
            this.$emit("parent-error", e);
          })
          .finally(() => {
            this.currentlySubmitting = false;
          });
      },
      resetForm() {
        this.submitted = false;
        this.error = false;
      },
      reloadForm() {
        this.submitted = false;
        this.error = false;
        this.formsData.fields.forEach((item) => (item.value = null));
        this.checkIfSubmittable();
      },
      checkIfSubmittable() {
        // const { paymentRequestToken } = this.$route.params; // should always be a string

        // @TODO validate paymentRequestToken too? Or just for submit?
        this.submitDisabled =
          this.form !== null &&
          this.form.fields !== undefined &&
          this.form.fields.find(
            (element) => element.required && !element.value
          ) !== undefined;
      },
      updateForceFocus(fieldId) {
        this.forceFocus = {
          fieldId: fieldId,
          sequence: this.forceFocus.sequence + 1,
        };
      },
    },
  };
</script>

<style scoped lang="scss">
/*We need to import the global styles for each view in order for the compiled web component to inherit the global and updox-styles styling*/
@import "@/assets/_scss/main";

  .form-container {
    background-color: #fff;
    padding: 15px;

    .form-field {
      display: flex;
      flex-direction: column;
      margin-top: 30px;

      &:first-child {
        margin-top: 0;
      }
    }

    .form-body {
      margin-bottom: 35px;

      ::placeholder {
        font-size: 12px;
      }
    }
    section.error {
      background: #ffeded;
      border: 1px solid rgba(173, 0, 0, 0.2);
      border-radius: 4px;
      margin-top: 5px;
      margin-bottom: 20px;
      padding: 5px;
    }

    section.warning {
      background: #fffee3;
      border: 1px solid rgba(255, 220, 0, 0.2);
      border-radius: 4px;
      margin-top: 20px;
      margin-bottom: 20px;
      padding: 5px;
    }

    .form-actions {
      p {
        font-size: 12px;
        margin: 0;
      }
      .updox-button {
        width: 152px;
      }
    }
  }
</style>
